import { NavLink, Link } from "react-router-dom";
import { Home, BookOpen, Sparkles, ClipboardCheck, NotebookPen, Trophy, MessageCircle, Plus, Sun, BookHeart, Crown, Images } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useChildren } from "@/context/ChildrenContext";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ProfileMenu from "@/components/ProfileMenu";
import TrialStatusBar from "@/components/TrialStatusBar";
import AccessGate from "@/components/AccessGate";
import MobileNav from "@/components/MobileNav";

const navItems = [
  { to: "/", label: "Accueil", icon: Home, testid: "nav-home" },
  { to: "/rituel", label: "Rituel", icon: Sun, testid: "nav-ritual" },
  { to: "/conseils", label: "Conseils", icon: BookOpen, testid: "nav-advice" },
  { to: "/activites", label: "Activités", icon: Sparkles, testid: "nav-activities" },
  { to: "/quiz", label: "Quiz", icon: ClipboardCheck, testid: "nav-quiz" },
  { to: "/journal", label: "Journal", icon: NotebookPen, testid: "nav-journal" },
  { to: "/defis", label: "Défis", icon: Trophy, testid: "nav-challenges" },
  { to: "/album", label: "Album", icon: Images, testid: "nav-album" },
  { to: "/contes", label: "Contes", icon: BookHeart, testid: "nav-stories" },
  { to: "/coach", label: "Coach IA", icon: MessageCircle, testid: "nav-coach" },
];

const COLORS = [
  { key: "sage", cls: "bg-[#D2EDE1]" },
  { key: "blush", cls: "bg-[#FBE2E9]" },
  { key: "sky", cls: "bg-[#E6F5EF]" },
  { key: "butter", cls: "bg-[#FFF4DC]" },
  { key: "lavender", cls: "bg-[#E3EAF7]" },
];

const EMOJIS = ["🧒", "👶", "👧", "👦", "🐣", "🦊", "🐻", "🦄", "🐰", "🐼", "🐨", "🦁", "🌟", "🌈", "🍀", "🐵"];

function AddChildDialog() {
  const { addChild } = useChildren();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [color, setColor] = useState("sage");
  const [emoji, setEmoji] = useState("🧒");
  const submit = async (e) => {
    e.preventDefault();
    if (!name || !age) return;
    await addChild({ name, age: parseInt(age, 10), avatar_color: color, avatar_emoji: emoji });
    toast.success(`Profil de ${name} créé`);
    setName(""); setAge(""); setEmoji("🧒"); setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-child-btn" className="flex items-center gap-1 rounded-full bg-[#FBE2E9] px-3 py-1.5 text-sm font-medium text-[#16233D] hover:bg-[#F8CDD8] transition">
          <Plus className="h-4 w-4" /> Enfant
        </button>
      </DialogTrigger>
      <DialogContent data-testid="add-child-dialog" className="rounded-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ajouter un enfant</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Prénom</Label>
            <Input data-testid="child-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Léo" required />
          </div>
          <div>
            <Label>Âge (années)</Label>
            <Input data-testid="child-age-input" type="number" min="0" max="20" value={age} onChange={(e) => setAge(e.target.value)} required />
          </div>
          <div>
            <Label className="mb-2 block">Avatar</Label>
            <div className="grid grid-cols-8 gap-2">
              {EMOJIS.map((em) => (
                <button
                  key={em}
                  type="button"
                  data-testid={`emoji-${em}`}
                  onClick={() => setEmoji(em)}
                  className={`h-9 w-9 rounded-xl text-xl grid place-items-center transition ${
                    emoji === em ? "bg-[#23375E] ring-2 ring-[#23375E]" : "bg-[#EDF2F9] hover:bg-[#E3E8F1]"
                  }`}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Couleur du profil</Label>
            <div className="flex gap-2">
              {COLORS.map((c) => (
                <button key={c.key} type="button" onClick={() => setColor(c.key)}
                  data-testid={`color-${c.key}`}
                  className={`h-9 w-9 rounded-full ${c.cls} ${color === c.key ? "ring-2 ring-[#23375E]" : ""}`} />
              ))}
            </div>
          </div>
          <Button type="submit" data-testid="save-child-btn" className="w-full brand-btn">Ajouter</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AppShell({ children }) {
  const { user } = useAuth();
  const { kids, activeChildId, setActiveChildId } = useChildren();
  const access = user?.access;

  return (
    <div className="min-h-screen" data-testid="app-shell">
      <header data-testid="app-header-nav" className="sticky top-0 z-40 backdrop-blur-md bg-[#FAF8F5]/80 border-b border-[#E3E8F1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 flex items-center justify-between gap-2">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
            <img src="/logo-kynoria.png" alt="Kynoria" className="h-9 w-9 sm:h-10 sm:w-10 object-contain" />
            <span className="text-lg sm:text-xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>Kynoria</span>
          </Link>
          <div className="flex items-center gap-2 min-w-0">
            <div className="hidden sm:flex items-center gap-2 flex-wrap">
              {kids.map((k) => (
                <button key={k.id} data-testid={`child-chip-${k.id}`} onClick={() => setActiveChildId(k.id)}
                  className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium border transition ${
                    activeChildId === k.id ? "bg-[#23375E] text-white border-[#23375E]" : "bg-white text-[#16233D] border-[#E3E8F1] hover:border-[#23375E]"
                  }`}>
                  <span className={`h-6 w-6 rounded-full pastel-${k.avatar_color} grid place-items-center text-sm`}>
                    {k.avatar_emoji || "🧒"}
                  </span>
                  {k.name} · {k.age} ans
                </button>
              ))}
              <AddChildDialog />
            </div>
            <Link
              to="/abonnement"
              data-testid="nav-subscribe"
              className={`flex items-center gap-1 rounded-full px-3 sm:px-4 py-1.5 text-sm font-semibold border transition-all duration-200 whitespace-nowrap ${
                access?.status === "active"
                  ? "bg-[#D2EDE1] text-[#16233D] border-[#23375E]"
                  : "bg-[#23375E] text-white border-[#23375E] hover:bg-[#16233D] shadow-sm hover:shadow-md hover:scale-[1.03]"
              }`}
            >
              <Crown className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">
                {access?.status === "active"
                  ? "Premium"
                  : access?.status === "bonus"
                  ? "Bonus"
                  : access?.status === "trial"
                  ? "Débloquer à vie"
                  : "Débloquer 47 €"}
              </span>
              <span className="sm:hidden">{access?.status === "active" ? "À vie" : "47 €"}</span>
            </Link>
            <ProfileMenu />
          </div>
        </div>

        {/* Enfants : ligne défilante dédiée sur mobile */}
        <div className="sm:hidden px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar" data-testid="mobile-children-row">
          {kids.map((k) => (
            <button key={k.id} data-testid={`child-chip-mobile-${k.id}`} onClick={() => setActiveChildId(k.id)}
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium border whitespace-nowrap transition ${
                activeChildId === k.id ? "bg-[#23375E] text-white border-[#23375E]" : "bg-white text-[#16233D] border-[#E3E8F1]"
              }`}>
              <span className={`h-6 w-6 rounded-full pastel-${k.avatar_color} grid place-items-center text-sm`}>
                {k.avatar_emoji || "🧒"}
              </span>
              {k.name} · {k.age} ans
            </button>
          ))}
          <AddChildDialog />
        </div>

        <nav className="hidden sm:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-2 overflow-x-auto no-scrollbar">
          <div className="flex gap-1">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === "/"} data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition ${
                    isActive ? "bg-[#23375E] text-white" : "text-[#16233D] hover:bg-[#E3E8F1]"
                  }`
                }>
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>
      <TrialStatusBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 fade-up">
        <AccessGate>{children}</AccessGate>
      </main>
      <footer className="max-w-7xl mx-auto px-4 py-8 pb-28 sm:pb-8 text-center text-sm text-[#77849C]">
        Kynoria · Fait avec 💛 pour les parents
      </footer>
      <MobileNav items={navItems} />
    </div>
  );
}
